/**
 * What an audit event was done to, as a reader would name it.
 *
 * @module components/shared/audit-target
 */

import type { AuditEvent } from "@/server/types";

/** A module's title and the programme it sits in. */
export interface AuditModuleName {
  title: string;
  programmeTitle: string;
}

/** The names a target id can resolve to. */
export interface AuditNames {
  users: ReadonlyMap<string, string>;
  programmes: ReadonlyMap<string, string>;
  modules: ReadonlyMap<string, AuditModuleName>;
  tenantName: string;
}

/** Action families whose target is a person. */
const USER_FAMILIES = new Set(["user", "role", "tenant-capabilities", "impersonation", "login"]);

/** Action families whose target is the tenant or its settings record. */
const TENANT_FAMILIES = new Set(["tenant", "settings"]);

/** The part of an action before its first dot: `module-owner.assigned` is `module-owner`. */
export function actionFamily(action: string): string {
  return action.split(".")[0] ?? action;
}

function metadataString(event: AuditEvent, key: string): string | undefined {
  const value = event.metadata?.[key];
  return typeof value === "string" ? value : undefined;
}

function moduleLabel(names: AuditNames, moduleId: string, event: AuditEvent): string | undefined {
  const known = names.modules.get(moduleId);
  if (known) {
    return `${known.title} (${known.programmeTitle})`;
  }
  // A deleted module keeps its events; its programme still says where it was.
  const programmeId = metadataString(event, "programmeId");
  const programme = programmeId ? names.programmes.get(programmeId) : undefined;
  return programme ? `A module in ${programme}` : undefined;
}

/**
 * The event's target as a reader would name it, or its raw id when nothing
 * resolves it. The id stays in the expanded detail either way.
 */
export function auditTargetLabel(event: AuditEvent, names: AuditNames): string {
  const family = actionFamily(event.action);
  const target = event.target;
  if (family === "module" || family === "module-owner") {
    return moduleLabel(names, target, event) ?? target;
  }
  if (family === "edit-lease") {
    const moduleId = metadataString(event, "moduleId");
    if (moduleId) {
      return moduleLabel(names, moduleId, event) ?? target;
    }
  }
  if (USER_FAMILIES.has(family)) {
    return names.users.get(target) ?? target;
  }
  if (TENANT_FAMILIES.has(family)) {
    return names.tenantName;
  }
  if (family === "invitation") {
    return metadataString(event, "email") ?? target;
  }
  return names.programmes.get(target) ?? names.users.get(target) ?? target;
}
